// The drag's browser half (#6771, epic #6552; prototype pointerdown through finish()
// `:2770-2916`): the window listeners, the frame loop, the hit test and the ghost's flights.
// Every decision is `./move-drag.ts`'s (the 7 px arm, Esc, the auto-scroll step, the ghost's
// motion); every verdict is the server's, through the page's move controller
// (`./move-page.ts`). `components/machine-card/DragLayer.tsx` renders what this shows.
//
// THE DRAG STARTS AT 7 PX and asks the ONE preview. While it is pending every other card reads
// "Checking…" and refuses a drop; a failed preview or a source refusal starts nothing (the
// ghost leaves, the row shakes 320 ms, a toast near it shows the sentence for 2.6 s, and the
// sentence is announced). A drop on a refusing card shakes that card (380 ms, outlined red
// 420 ms) and flies the ghost back (440 ms); a drop on an accepting one is ONE send through
// the page guard, and on its 201 the ghost settles onto the target's Now or Mission zone
// (480 ms). Esc flies it back and announces "Move cancelled". A click right after a drop is
// swallowed for 80 ms.
//
// REDUCED MOTION IS A STILL FRAME (`./reduced-motion.ts`): the ghost sits on the pointer with
// no easing, tilt or lift, and there is no fly-back, settle or shake.

import { t } from '../i18n';
import type { MoveRefusalWire } from './move-calls';
import {
  autoScrollStep,
  CLICK_SWALLOW_MS,
  type DragEvent,
  type DragState,
  dragStep,
  FLY_BACK_EASING,
  FLY_BACK_MS,
  type GhostMotion,
  ghostMotion,
  ghostTransform,
  ghostWidth,
  IDLE,
  REJECT_OUTLINE_MS,
  REST_MOTION,
  ROW_SHAKE_MS,
  SETTLE_EASING,
  SETTLE_MS,
} from './move-drag';
import type { MoveSubject } from './move-items';
import type { MovePage, MovePress } from './move-page';
import { orderStamp } from './read-order';
import { prefersReducedMotion } from './reduced-motion';

/** What the layer shows of the drag. */
export type GhostView = {
  readonly subject: MoveSubject;
  readonly width: number;
  /** An epic or a group with more than one job: a stacked ghost. */
  readonly stacked: boolean;
};

/** The layer's half: the ghost element, and what it shows. */
export type DragView = {
  readonly ghostEl: () => HTMLElement | undefined;
  readonly showGhost: (g: GhostView | null) => void;
  /** The ghost's border: accent over a card that can take it, red over one that cannot. */
  readonly setTone: (tone: 'ok' | 'no' | null) => void;
  /** The 2.6 s toast near the row, at viewport `left` / `top`. */
  readonly toast: (text: string, at: { readonly left: number; readonly top: number }) => void;
};

type Ctx = {
  readonly page: MovePage;
  readonly view: DragView;
  state: DragState;
  motion: GhostMotion;
  press: MovePress | null;
  dragging: MoveSubject | null;
  pointer: { x: number; y: number };
  grab: { x: number; y: number };
  raf: number;
  swallowUntil: number;
  reduced: boolean;
  listeners: { move: (e: PointerEvent) => void; up: (e: PointerEvent) => void };
  cancel: (e: PointerEvent) => void;
  key: (e: KeyboardEvent) => void;
  /**
   * Which press this is: bumped by every new press and by dispose. Every async callback (the
   * preview's answer, the move's answer, an animation's end) captures it and does nothing once
   * it moved, so a late answer never finishes a newer drag (review on #7779).
   */
  gen: number;
  /** When this drag started (`orderStamp`): orders a refusal's transfer card (#6773). */
  order: number;
  disposed: boolean;
  /** The flight in progress (fly-back or settle), cancelled on dispose. */
  anim: Animation | null;
};

/** The press `gen` is still this engine's current one. */
const current = (c: Ctx, gen: number): boolean => !c.disposed && c.gen === gen;

/** Each card's red-outline timer: a second refused drop restarts it rather than racing it. */
const rejectTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();

const animate = (
  el: Element | null | undefined,
  frames: Keyframe[],
  opts: KeyframeAnimationOptions,
): Animation | null => (el == null ? null : (el.animate?.(frames, opts) ?? null));

function stopLoop(c: Ctx): void {
  if (c.raf !== 0) globalThis.cancelAnimationFrame?.(c.raf);
  c.raf = 0;
}

function disarm(c: Ctx): void {
  window.removeEventListener('pointermove', c.listeners.move);
  window.removeEventListener('pointerup', c.listeners.up);
  window.removeEventListener('pointercancel', c.cancel);
  document.removeEventListener('keydown', c.key, true);
}

/** The drag is over: the ghost leaves, the row comes back, the controller forgets it. */
function finish(c: Ctx): void {
  stopLoop(c);
  disarm(c);
  c.view.showGhost(null);
  c.view.setTone(null);
  c.press?.row.classList.remove('move-src');
  document.body.classList.remove('move-dragging');
  c.page.endDrag();
  c.page.release();
  c.press = null;
  c.dragging = null;
  c.state = IDLE;
}

/** Run `a` to its end, then finish; none (reduced motion, no WAAPI) finishes now. */
function thenFinish(c: Ctx, a: Animation | null): void {
  if (a === null) {
    finish(c);
    return;
  }
  const gen = c.gen;
  c.anim = a;
  a.onfinish = () => {
    if (!current(c, gen)) return;
    c.anim = null;
    finish(c);
  };
}

const restAt = (left: number, top: number): string =>
  `translate3d(${left}px,${top}px,0) rotate(0deg) scale(1)`;

/** Esc, a refused drop or a refused move: the ghost flies back to its row in 440 ms. */
function flyBack(c: Ctx): void {
  const row = c.press?.row;
  const r = row?.isConnected === true ? row.getBoundingClientRect() : null;
  const el = c.view.ghostEl();
  const a =
    r === null || c.reduced
      ? null
      : animate(
          el,
          [{ transform: el?.style.transform ?? '' }, { transform: restAt(r.left, r.top) }],
          {
            duration: FLY_BACK_MS,
            easing: FLY_BACK_EASING,
            fill: 'forwards',
          },
        );
  thenFinish(c, a);
}

/** A 201: the ghost settles onto the landed row, else the target's Now or Mission zone. */
function settle(c: Ctx, subject: MoveSubject, targetId: string): void {
  const card = document.querySelector(`[data-move-card="${targetId}"]`);
  const rowKey = `${targetId}|${subject.item.kind}:${subject.item.id}`;
  const landed = card?.querySelector(`[data-move-row="${rowKey}"]`) ?? null;
  const zone = card?.querySelector(
    `[data-move-zone="${subject.item.kind === 'epic' ? 'mission' : 'now'}"]`,
  );
  const r = (landed ?? zone ?? card)?.getBoundingClientRect() ?? null;
  const el = c.view.ghostEl();
  if (r === null || c.reduced) {
    thenFinish(c, null);
    return;
  }
  const end = landed === null ? restAt(r.left + 16, r.top + 12) : restAt(r.left, r.top);
  const a = animate(
    el,
    [
      { transform: el?.style.transform ?? '', opacity: 1 },
      { transform: end, opacity: 1, offset: 0.78 },
      { transform: end, opacity: 0 },
    ],
    { duration: SETTLE_MS, easing: SETTLE_EASING, fill: 'forwards' },
  );
  thenFinish(c, a);
}

const SHAKE: Keyframe[] = [
  { transform: 'translateX(0)' },
  { transform: 'translateX(-4px)' },
  { transform: 'translateX(4px)' },
  { transform: 'translateX(-2px)' },
  { transform: 'translateX(0)' },
];

/**
 * A source refusal or a failed preview: nothing starts; a refusal shakes, toasts and says why,
 * and its refusal OBJECT is handed to the page's refusal hook (#6773). A failed preview carries
 * none and sent nothing: no card.
 */
function refuseAtSource(c: Ctx, why: string | null, refusal: MoveRefusalWire | null): void {
  const row = c.press?.row ?? null;
  const subject = c.dragging;
  if (refusal !== null && subject !== null) c.page.dragRefused(subject, null, refusal, c.order);
  if (why !== null && row !== null) {
    if (!c.reduced) animate(row, SHAKE, { duration: ROW_SHAKE_MS, easing: 'ease-out' });
    const r = row.getBoundingClientRect();
    c.view.toast(why, {
      left: Math.max(8, Math.min(window.innerWidth - 288, r.left)),
      top: Math.max(8, r.top - 46),
    });
    c.page.announce(why);
  }
  finish(c);
}

/**
 * A drop on a refusing card: it shakes (CSS, still under reduced motion), outlined red. The
 * standing refusal `why` renders, read before `endDrag` cleared the session, goes to the page's
 * refusal hook (#6773).
 */
function rejectOn(
  c: Ctx,
  subject: MoveSubject,
  targetId: string,
  why: string,
  refusal: MoveRefusalWire | null,
): void {
  if (refusal !== null) c.page.dragRefused(subject, targetId, refusal, c.order);
  const card = document.querySelector<HTMLElement>(`[data-move-card="${targetId}"]`);
  if (card !== null) {
    card.classList.add('mission-card--reject');
    const prior = rejectTimers.get(card);
    if (prior !== undefined) clearTimeout(prior);
    rejectTimers.set(
      card,
      setTimeout(() => {
        rejectTimers.delete(card);
        card.classList.remove('mission-card--reject');
      }, REJECT_OUTLINE_MS),
    );
  }
  c.page.announce(why);
}

/** Which card the pointer is over, and how that card answers. */
function hit(c: Ctx, subject: MoveSubject): void {
  const el = document.elementFromPoint?.(c.pointer.x, c.pointer.y) ?? null;
  const card = el?.closest<HTMLElement>('[data-move-card]') ?? null;
  const id = card?.dataset['moveCard'] ?? null;
  if (card === null || id === null || id === subject.sourceId) {
    c.page.setOver(null);
    c.view.setTone(null);
    return;
  }
  c.page.setOver(id, c.pointer.y - card.getBoundingClientRect().top);
  const v = c.page.targetView(id);
  c.view.setTone(v === null || v.kind === 'checking' ? null : v.why === null ? 'ok' : 'no');
}

function frame(c: Ctx): void {
  const subject = c.dragging;
  if (subject === null) return;
  const at = { x: c.pointer.x - c.grab.x, y: c.pointer.y - c.grab.y };
  c.motion = ghostMotion(c.motion, at, c.reduced);
  const el = c.view.ghostEl();
  if (el !== undefined) el.style.transform = ghostTransform(c.motion);
  const dy = autoScrollStep(c.pointer.y, window.innerHeight);
  if (dy !== 0) window.scrollBy?.(0, dy);
  hit(c, subject);
  c.raf = globalThis.requestAnimationFrame?.(() => frame(c)) ?? 0;
}

/** The press crossed 7 px: show the ghost, announce, and ask the preview. */
function start(c: Ctx, p: MovePress): void {
  c.reduced = prefersReducedMotion();
  c.order = orderStamp();
  const subject = p.subject;
  const r = p.row.getBoundingClientRect();
  const width = ghostWidth(r.width);
  c.grab = {
    x: Math.max(14, Math.min(p.event.clientX - r.left, width - 20)),
    y: Math.max(12, Math.min(p.event.clientY - r.top, 30)),
  };
  c.motion = { ...REST_MOTION, gx: r.left, gy: r.top, lx: c.pointer.x };
  c.dragging = subject;
  c.view.showGhost({
    subject,
    width,
    stacked: subject.item.kind !== 'task' && subject.jobs.length > 1,
  });
  window.getSelection?.()?.removeAllRanges();
  p.row.classList.add('move-src');
  document.body.classList.add('move-dragging');
  c.page.announce(t('fleet.move.announce_start', { label: subject.label }));
  c.raf = globalThis.requestAnimationFrame?.(() => frame(c)) ?? 0;
  const gen = c.gen;
  void c.page.beginDrag(subject).then((answer) => {
    if (!current(c, gen) || c.press !== p || c.state.phase !== 'dragging') return;
    if (answer.status === 'failed') refuseAtSource(c, null, null);
    else if (answer.status === 'ready') {
      // Both read now, while the session still holds the preview (`finish` ends it).
      const why = c.page.sourceWhy();
      if (why !== null) refuseAtSource(c, why, c.page.sourceRefusal());
    }
  });
}

/** Let go over a card: refuse with the sentence, or send the move. */
function drop(c: Ctx): void {
  const subject = c.dragging;
  if (subject === null) {
    finish(c);
    return;
  }
  stopLoop(c);
  c.swallowUntil = Date.now() + CLICK_SWALLOW_MS;
  const over = c.page.over();
  // A card with no drop target (another org's, a read-only one) takes nothing, whatever the
  // preview names.
  const target =
    over === null ? null : document.querySelector(`[data-move-card="${over.id}"] [data-move-drop]`);
  const view = over === null || target === null ? null : c.page.targetView(over.id);
  const blocked = c.page.blockedWhy(subject.sourceId);
  c.page.endDrag();
  if (blocked !== null && over !== null) {
    // Disabled during the drag (the move limit read 0, or the page locked): nothing is sent.
    c.page.announce(blocked);
    flyBack(c);
    return;
  }
  if (over === null || view === null || view.kind === 'checking') {
    flyBack(c);
    return;
  }
  if (view.why !== null) {
    rejectOn(c, subject, over.id, view.why, view.refusal);
    flyBack(c);
    return;
  }
  const gen = c.gen;
  void c.page.move(subject, over.id).then((outcome) => {
    if (!current(c, gen)) return;
    if (outcome.kind === 'moved') settle(c, subject, over.id);
    else flyBack(c);
  });
}

function step(c: Ctx, e: DragEvent): ReturnType<typeof dragStep> {
  const next = dragStep(c.state, e);
  c.state = next.state;
  return next;
}

const mine = (c: Ctx, e: PointerEvent): boolean =>
  c.press !== null && e.pointerId === c.press.event.pointerId;

function onMove(c: Ctx, e: PointerEvent): void {
  if (!mine(c, e) || c.press === null) return;
  c.pointer = { x: e.clientX, y: e.clientY };
  const { effect } = step(c, { type: 'move', pointerId: e.pointerId, x: e.clientX, y: e.clientY });
  if (effect === 'start') start(c, c.press);
  if (c.state.phase === 'dragging') e.preventDefault();
}

function onUp(c: Ctx, e: PointerEvent): void {
  if (!mine(c, e)) return;
  const { effect } = step(c, { type: 'up', pointerId: e.pointerId });
  if (effect === 'drop') {
    // The drop lands where the pointer is RELEASED: hit-test its own coordinates now, not the
    // card the last frame saw (a pointerup can arrive before the next frame runs).
    c.pointer = { x: e.clientX, y: e.clientY };
    if (c.dragging !== null) hit(c, c.dragging);
    drop(c);
  } else if (c.dragging === null) {
    // A press that never travelled 7 px stays a click: let go of it.
    disarm(c);
    c.page.release();
    c.press = null;
  }
}

function onCancel(c: Ctx, e: PointerEvent): void {
  if (!mine(c, e)) return;
  const { effect } = step(c, { type: 'cancel', pointerId: e.pointerId });
  stopLoop(c);
  c.page.endDrag();
  if (effect === 'fly_back') flyBack(c);
  else finish(c);
}

function onKey(c: Ctx, e: KeyboardEvent): void {
  if (e.key !== 'Escape') return;
  if (c.state.phase === 'pressed') {
    // A press that has not travelled 7 px yet: Esc lets go of it, and nothing started.
    step(c, { type: 'escape' });
    disarm(c);
    c.page.release();
    c.press = null;
    return;
  }
  if (c.state.phase !== 'dragging') return;
  e.preventDefault();
  e.stopPropagation();
  step(c, { type: 'escape' });
  stopLoop(c);
  c.page.endDrag();
  c.page.announce(t('fleet.move.announce_cancelled'));
  flyBack(c);
}

/** The page's drag: arm it with a press; `swallow` is the document's capture click listener. */
export function createDragEngine(page: MovePage, view: DragView) {
  const c: Ctx = {
    page,
    view,
    state: IDLE,
    motion: REST_MOTION,
    press: null,
    dragging: null,
    pointer: { x: 0, y: 0 },
    grab: { x: 0, y: 0 },
    raf: 0,
    swallowUntil: 0,
    reduced: false,
    listeners: { move: (e) => onMove(c, e), up: (e) => onUp(c, e) },
    cancel: (e) => onCancel(c, e),
    key: (e) => onKey(c, e),
    gen: 0,
    order: 0,
    disposed: false,
    anim: null,
  };
  return {
    /** A row was pressed: follow that pointer until it lets go. */
    arm(p: MovePress): void {
      if (c.disposed || c.press !== null) return;
      c.gen += 1;
      c.press = p;
      c.pointer = { x: p.event.clientX, y: p.event.clientY };
      c.state = IDLE;
      step(c, { type: 'down', pointerId: p.event.pointerId, x: c.pointer.x, y: c.pointer.y });
      window.addEventListener('pointermove', c.listeners.move, { passive: false });
      window.addEventListener('pointerup', c.listeners.up);
      window.addEventListener('pointercancel', c.cancel);
      document.addEventListener('keydown', c.key, true);
    },
    /** A click right after a drop is swallowed, so the drop does not also press what is under it. */
    swallow(e: MouseEvent): void {
      if (Date.now() >= c.swallowUntil) return;
      e.preventDefault();
      e.stopPropagation();
      c.swallowUntil = 0;
    },
    /**
     * The page left: stop the frame loop, the listeners and any flight, and invalidate every
     * answer still in flight (`gen`). Only this engine's own drag gives back the body class.
     */
    dispose(): void {
      c.disposed = true;
      c.gen += 1;
      stopLoop(c);
      disarm(c);
      c.anim?.cancel();
      c.anim = null;
      if (c.dragging !== null) {
        c.press?.row.classList.remove('move-src');
        document.body.classList.remove('move-dragging');
      }
      c.press = null;
      c.dragging = null;
      c.state = IDLE;
    },
  };
}
